import { useEffect } from "react";
import { HOME_MEDIA, mediaUrl, publicMediaSrc, toAbsoluteUrl } from "../config/api";

function upsertMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el && el.dataset.homeSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.dataset.homeSeo = "true";
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el && el.dataset.homeSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  el.dataset.homeSeo = "true";
}

function socialUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw.replace(/^\/+/, "")}`;
}

function upsertJsonLd(data) {
  const id = "home-jsonld";
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function HomeSeo({ contact }) {
  useEffect(() => {
    const siteName = String(contact?.siteName || "").trim();
    const companyName = String(contact?.companyName || "").trim();
    const emailAddress = String(contact?.emailAddress || "").trim();
    const whatsappNumber = String(contact?.whatsappNumber || "").trim();
    const address = String(contact?.address || "").trim();
    const instagram = String(contact?.instagram || "").trim();
    const facebook = String(contact?.facebook || "").trim();
    const logoPath = publicMediaSrc(contact?.siteLogo) || mediaUrl(HOME_MEDIA.logo);
    const pageUrl = toAbsoluteUrl("/");
    const imageUrl = toAbsoluteUrl(logoPath);

    const title = siteName
      ? `Steel, Hardware & Industrial Supplies Malaysia | ${siteName}`
      : "Steel, Hardware & Industrial Supplies Malaysia";
    const description = siteName
      ? `${siteName} supplies steel, hardware, fasteners, roofing, paint and industrial products to contractors across Malaysia. Request a quote today.`
      : "Steel, hardware, fasteners, roofing, paint and industrial products for contractors across Malaysia. Request a quote today.";

    document.title = title;
    upsertMeta("name", "description", description);
    upsertLink("canonical", pageUrl);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", pageUrl);
    upsertMeta("property", "og:image", imageUrl);
    if (siteName) upsertMeta("property", "og:site_name", siteName);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", imageUrl);

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": address || whatsappNumber ? "LocalBusiness" : "Organization",
      "@id": pageUrl ? `${String(pageUrl).replace(/\/$/, "")}/#organization` : undefined,
      name: companyName || siteName || undefined,
      url: pageUrl || undefined,
      logo: imageUrl || undefined,
      image: imageUrl || undefined,
      email: emailAddress || undefined,
      telephone: whatsappNumber || undefined,
      address: address
        ? {
            "@type": "PostalAddress",
            streetAddress: address,
          }
        : undefined,
      sameAs: [socialUrl(instagram), socialUrl(facebook)].filter(Boolean),
    };
    Object.keys(jsonLd).forEach((key) => {
      if (jsonLd[key] === undefined || (Array.isArray(jsonLd[key]) && jsonLd[key].length === 0)) {
        delete jsonLd[key];
      }
    });
    upsertJsonLd(jsonLd);

    return () => {
      document.head.querySelectorAll("[data-home-seo='true']").forEach((el) => el.remove());
      const script = document.getElementById("home-jsonld");
      if (script) script.remove();
    };
  }, [contact]);

  return null;
}

export default HomeSeo;
